// REST client: bearer access token in memory, single-flight refresh on 401, typed errors.
import type { AuthResult } from '@moods/shared';
import { API_URL, IS_DESKTOP } from './env';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
  get retryAfter(): number | undefined {
    return (this.details as { retryAfter?: number } | undefined)?.retryAfter;
  }
}

type SessionResult = Extract<AuthResult, { kind: 'session' }>;

interface TokenHooks {
  getToken: () => string | null;
  onRefreshed: (r: SessionResult) => void;
  onExpired: () => void;
  /** desktop keeps the refresh token in secure storage */
  getStoredRefreshToken?: () => Promise<string | null>;
  storeRefreshToken?: (token: string | null) => Promise<void>;
}

let hooks: TokenHooks | null = null;
export function configureApi(h: TokenHooks): void {
  hooks = h;
}

let refreshing: Promise<SessionResult | null> | null = null;
/** Why the last refresh returned null: the session is gone (401/403) or the API was unreachable. */
let refreshFailure: 'unauthorized' | 'network' | null = null;
export const lastRefreshFailure = (): 'unauthorized' | 'network' | null => refreshFailure;

/** POST /auth/refresh once even if many requests 401 at the same time. */
export function refreshSession(): Promise<SessionResult | null> {
  refreshing ??= withRefreshLock(async () => {
    try {
      const stored = IS_DESKTOP ? await hooks?.getStoredRefreshToken?.() : null;
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: stored ? { 'content-type': 'application/json' } : {},
        body: stored ? JSON.stringify({ refreshToken: stored }) : undefined,
      });
      if (!res.ok) {
        refreshFailure = res.status === 401 || res.status === 403 ? 'unauthorized' : 'network';
        return null;
      }
      const out = (await res.json()) as SessionResult;
      refreshFailure = null;
      if (out.refreshToken) await hooks?.storeRefreshToken?.(out.refreshToken);
      hooks?.onRefreshed(out);
      return out;
    } catch {
      refreshFailure = 'network';
      return null;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  });
  return refreshing;
}

/** Serializes refreshes across tabs (Web Locks) so parallel tabs don't race the refresh-token rotation. */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) return navigator.locks.request('moods-refresh', fn) as Promise<T>;
  return fn();
}

export interface RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /** skip the automatic refresh-and-retry (auth endpoints) */
  noRetry?: boolean;
  headers?: Record<string, string>;
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { error?: { code: string; message: string; details?: unknown } };
    if (body.error) return new ApiError(res.status, body.error.code, body.error.message, body.error.details);
  } catch {
    // not JSON
  }
  return new ApiError(res.status, res.status >= 500 ? 'INTERNAL' : 'UNKNOWN', res.statusText || 'İstek başarısız oldu.');
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${API_URL}/api/v1${path}`);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { ...opts.headers };
    const token = hooks?.getToken();
    if (token) headers.authorization = `Bearer ${token}`;
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    try {
      return await fetch(url, {
        method,
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        credentials: 'include',
        signal: opts.signal,
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      throw new ApiError(0, 'NETWORK', 'Sunucuya ulaşılamıyor. Bağlantını kontrol et.');
    }
  };
  let res = await send();
  if (res.status === 401 && !opts.noRetry && hooks?.getToken()) {
    const refreshed = await refreshSession();
    if (refreshed) res = await send();
    else hooks?.onExpired();
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, { ...opts, body }),
  delete: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('DELETE', path, { ...opts, body }),
};
